import styled from "styled-components";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import appConfig from "../../lib/appConfig";
import CloseButton from "../../components/atoms/CloseButton";
import { ReactComponent as ArrowRightIcon } from "../../assets/svg/arrow-right.svg";
import FilledButton, {
  FilledButtonAlt,
} from "../../components/atoms/FilledButton";
import colours from "../../lib/colours";
import { removalReportOutputParams as params } from "../../lib/gpConfig";
import AppRequirements from "../Other/AppRequirements";
import ReviewDownloadRemoval from "../Other/ReviewDownloadRemoval";
import ReviewDownloadOffset from "../Other/ReviewDownloadOffset";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import {
  useAppRequirementsContext,
  useDialogContext,
  useFeaturesContext,
} from "../../context/ContextProvider";
import DialogCallout from "../../components/other/DialogCallout";
import { initialDialogProps } from "../Other/DialogContainer";
import { AppRequirementsState } from "../../context/appRequirementsReducer";
import { IState } from "../../context/featuresReducer";
import {
  Patch,
  RevegetationArea,
  Tree,
  nvrTools,
  processingStatuses,
} from "../../lib/types";
import {
  checkReportStatus,
  downloadReport,
  requestRemovalReport,
  requestOffsetReport,
} from "../../utils/requests";
import SpinnerBase from "../../components/atoms/SpinnerBase";
import useInterval from "../../utils/useInterval";
import TextButton from "../../components/atoms/TextButton";
import useExportFeatures from "../../utils/useExportFeatures";

const navHeight = "106";

const Container = styled.div`
  height: calc(90vh - 40px);
  width: calc(90vw - 80px);
  max-width: 1000px;
  display: grid;
  grid-template-columns: [start] 1fr [end];
  grid-template-rows: [start] ${navHeight}px 1fr auto [end];
  grid-template-areas:
    "top"
    "scrollableContent"
    "footer";
  @media (max-width: ${appConfig.tabletWidth}px) {
    height: calc(100vh - 40px);
    width: calc(100vw - 80px);
  }
`;

const TopSection = styled.div`
  grid-area: top;
  padding-inline: 16px;
`;

const Nav = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media (max-width: ${appConfig.tabletWidth}px) {
    margin-right: -10px;
  }
`;

const HeaderContainer = styled.header`
  height: 46px;
  width: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

const TextContainer = styled.div`
  flex: 1;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.4px;
  color: ${colours.th1};
`;

const ScrollableContent = styled.div`
  grid-area: scrollableContent;
  padding-inline: 16px;
  overflow: auto;
  color: ${colours.darkerGrey};
  font-weight: 300;
  letter-spacing: -0.01875rem;
`;

const Footer = styled.div`
  grid-area: footer;
  padding-inline: 16px;
  padding-block: 24px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackButton = styled(FilledButtonAlt)`
  color: ${colours.th1l2};
  padding: 10px;
  display: flex;
  align-items: center;
  position: relative;
  left: -14px;
  &:hover {
    color: ${colours.th1};
    background-color: transparent;
  }
`;

const BackArrow = styled(ArrowRightIcon)`
  height: 16px;
  margin-right: 10px;
  transform: rotate(180deg);
`;

const FooterButton = styled(FilledButton)`
  display: flex;
  align-items: center;
  height: auto;
  padding: 14px 40px;
  min-width: min(80vw, 386px);
  justify-content: center;

  &:not(:first-child) {
    margin-top: 10px;
  }
`;

const FooterButtonAlt = styled(FooterButton)`
  background-color: ${colours.white};
  border: 1px solid ${colours.grey3};
  &:hover {
    background-color: ${colours.offWhite};
  }
  &:hover {
    background-color: ${colours.offWhite};
  }
  &:disabled {
    background-color: ${colours.offWhite};
    color: ${colours.th1l2};
    cursor: not-allowed;
    &:hover {
      background-color: ${colours.offWhite};
    }
  }
`;

const DownloadErrorDiv = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colours.errord1};
  font-weight: 500;
`;

const TryAgainButton = styled(TextButton)`
  font-size: 16px;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  margin-left: 4px;
`;

const FooterArrowIcon = styled(ArrowRightIcon)`
  height: 14px;
  margin-left: 10px;
`;

const ItalicsSpan = styled.span`
  font-style: italic;
`;

const spinnerStyle = {
  display: "flex",
  alignItems: "center",
  marginLeft: "6px",
  width: "auto",
};

const spinnerChildrenStyle = {
  width: "8px",
  height: "8px",
};

const spinnerChildrenStyleLight = {
  backgroundColor: colours.th1l2,
  ...spinnerChildrenStyle,
};

const missingPropsText = (
  <span>
    All applications for approval must address the application requirements
    outlined in Table 4 of the{" "}
    <ItalicsSpan>
      Guidelines for the removal, destruction or lopping of native vegetation
    </ItalicsSpan>
    . Any omitted information will need to be included separately within an
    application.
  </span>
);
const confirmText = `Please click "Yes" if you are confident that you have explored and implemented all practical options to avoid and minimise native vegetation removal, and there are no further opportunities to reduce the impacts of your proposal.`;

const pollInterval = 2000;

interface IProps {
  mode: nvrTools;
  onClose: any;
  removalResults: any;
  pastRemovalNumber: number | undefined;
}

function Final({ mode, onClose, removalResults, pastRemovalNumber }: IProps) {
  const state: AppRequirementsState = useAppRequirementsContext();
  const featuresState: IState = useFeaturesContext();
  const [showApplicationRequirements, setShowApplicationRequirements] =
    useState(true);
  const [detailedPathway, setDetailedPathway] = useState(false);
  const [page, setPage] = useState<number>(0);

  const [exportingShapefile, setExportingShapefile] = useState(false);
  const [preparingReport, setPreparingReport] = useState(false);
  const [reportId, setReportId] = useState<string>();
  const [processingStatus, setProcessingStatus] =
    useState<processingStatuses>();
  const [reportFilename, setReportFilename] = useState<string>();
  const [exportError, setExportError] = useState(false);
  const [reportError, setReportError] = useState(false);

  const exportToShapefile = useExportFeatures({
    setExporting: setExportingShapefile,
    setExportError,
  });

  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();

  const { start: startPolling, stop: stopPolling } = useInterval(() => {
    pollReport();
  }, pollInterval);

  const abortPolling = useCallback(() => {
    stopPolling();
    setPreparingReport(false);
  }, [stopPolling]);

  const pollReport = useCallback(async () => {
    if (
      !reportId ||
      ![processingStatuses.processing, undefined].includes(processingStatus)
    )
      return;
    checkReportStatus(reportId)
      .then((response) => {
        setProcessingStatus(response.processingStatus);
        setReportFilename(response.filename);
        if (response.processingStatus === processingStatuses.completed) {
          stopPolling();
        }
        if (response.processingStatus === processingStatuses.failed) {
          setReportError(true);
          abortPolling();
        }
      })
      .catch((e: any) => {
        console.error(e);
        setReportError(true);
        abortPolling();
      });
  }, [reportId, processingStatus, abortPolling, stopPolling]);

  // handle completed/failed report
  useEffect(() => {
    if (!reportId || !reportFilename) return;
    const handleError = () => {
      setReportError(true);
      setReportId(undefined);
      setProcessingStatus(undefined);
    };
    if (processingStatus === processingStatuses.completed) {
      setTimeout(() => {
        downloadReport(reportId, reportFilename)
          .then(() => {
            setReportId(undefined);
            setReportFilename(undefined);
            setProcessingStatus(undefined);
            setPreparingReport(false);
          })
          .catch((e: any) => {
            console.error(e);
            handleError();
          });
      }, 4000);
    }
    if (processingStatus === processingStatuses.failed) {
      handleError();
    }
  }, [reportId, reportFilename, processingStatus]);

  const requestReport = () => {
    setPreparingReport(true);
    setReportError(false);
    const patches = featuresState.patchesList.map(
      (x) => featuresState.features[x] as Patch
    );
    const trees = featuresState.treesList.map(
      (x) => featuresState.features[x] as Tree
    );
    const revegetationAreas = featuresState.revegetationAreasList.map(
      (x) => featuresState.features[x] as RevegetationArea
    );

    const handleError = (e: any) => {
      console.error(e);
      setReportError(true);
      abortPolling();
    };

    if (mode === nvrTools.removal) {
      requestRemovalReport(patches, trees, pastRemovalNumber, state)
        .then((response: any) => {
          setReportId(response.reportJobId);
          startPolling();
        })
        .catch(handleError);
    } else {
      requestOffsetReport(patches, trees, revegetationAreas)
        .then((response: any) => {
          setReportId(response.reportJobId);
          startPolling();
        })
        .catch(handleError);
    }
  };

  const pathway = removalResults?.results[params.assessmentPathway.index].value;
  const backButtonLabel =
    page === 1 && showApplicationRequirements
      ? "application requirements"
      : "summary";
  const headerLabel =
    page === 0 ? "Application Requirements" : "Review and download";

  const anyUnfilledAppRequirements = useMemo(() => {
    const mandatoryProps = Object.keys(state).filter(
      (x) => x !== "nativeVegPrecinctPlanText"
    );
    const anyUnfilledProps = mandatoryProps.some((x) =>
      // @ts-ignore
      [null, ""].includes(state[x])
    );
    if (anyUnfilledProps) return true;
    // additionally check for missing nativeVegPrecinctPlanText when it is expected
    return (
      state.nativeVegPrecinctPlan && state.nativeVegPrecinctPlanText === ""
    );
  }, [state]);

  const onNextButtonClick = () => {
    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
      },
      heading: "Avoid and Minimise Impacts Acknowledgement",
      children: (
        <>
          {anyUnfilledAppRequirements && (
            <DialogCallout
              calloutConfigs={[{ type: "warning", content: missingPropsText }]}
            ></DialogCallout>
          )}
          <div style={{ marginTop: anyUnfilledAppRequirements ? "16px" : 0 }}>
            {confirmText}
          </div>
        </>
      ),
      additionalButtons: [
        {
          text: "Yes",
          primary: true,
          onClickHandler: () => {
            setPage(1);
            setDialogProps(initialDialogProps);
          },
        },
      ],
      closeButtonText: "No",
      showXButton: false,
    });
  };

  const onDialogClose = () => {
    if (showApplicationRequirements) {
      setPage(0);
    }
    setPreparingReport(false);
    setExportingShapefile(false);
    setReportId(undefined);
    setProcessingStatus(undefined);
    setReportFilename(undefined);
    setReportError(false);
    setExportError(false);
    onClose();
  };

  const onBackButtonClick = () => {
    if (page === 1 && showApplicationRequirements) {
      setPage(0);
    } else {
      onDialogClose();
    }
  };

  // initialise state
  useEffect(() => {
    const isDetailed = pathway === "Detailed";
    setDetailedPathway(isDetailed);
    if (isDetailed || mode === nvrTools.offset) {
      setShowApplicationRequirements(false);
      setPage(1);
    } else {
      setPage(0);
    }
  }, [setDetailedPathway, pathway, mode]);

  const renderDownloadReportAction = () => {
    if (detailedPathway) return null;
    if (reportError)
      return (
        <DownloadErrorDiv>
          Report could not be generated.{" "}
          <TryAgainButton onClick={requestReport}>Try again</TryAgainButton>
        </DownloadErrorDiv>
      );
    return (
      <FooterButton
        onClick={requestReport}
        disabled={detailedPathway || preparingReport}
      >
        <span>
          {preparingReport ? "Preparing report" : "Download Report (PDF)"}
        </span>
        {preparingReport && (
          <SpinnerBase
            style={spinnerStyle}
            childrenStyle={spinnerChildrenStyle}
          />
        )}
      </FooterButton>
    );
  };

  const renderExportButton = () => {
    if (exportError)
      return (
        <DownloadErrorDiv style={{ height: "52px", marginTop: "10px" }}>
          Export could not be generated.{" "}
          <TryAgainButton onClick={exportToShapefile}>Try again</TryAgainButton>
        </DownloadErrorDiv>
      );
    return (
      <FooterButtonAlt
        onClick={exportToShapefile}
        disabled={exportingShapefile}
      >
        <span>
          {exportingShapefile ? "Preparing export" : "Export Features"}
        </span>
        {exportingShapefile && (
          <SpinnerBase
            style={spinnerStyle}
            childrenStyle={spinnerChildrenStyleLight}
          />
        )}
      </FooterButtonAlt>
    );
  };

  const renderReviewDownloadContent = () => {
    return mode === nvrTools.removal ? (
      <ReviewDownloadRemoval pathway={pathway} />
    ) : (
      <ReviewDownloadOffset />
    );
  };

  return (
    <Container>
      <TopSection>
        <Nav>
          <BackButton onClick={onBackButtonClick}>
            <BackArrow />
            <span>Back to {backButtonLabel}</span>
          </BackButton>
          <CloseButton onClick={onDialogClose} />
        </Nav>
        <HeaderContainer>
          <TextContainer>{headerLabel}</TextContainer>
        </HeaderContainer>
      </TopSection>

      <ScrollableContent>
        {page === 0 ? <AppRequirements /> : renderReviewDownloadContent()}
      </ScrollableContent>

      <Footer>
        {page === 0 ? (
          <FooterButton onClick={onNextButtonClick}>
            <span>Next</span>
            <FooterArrowIcon />
          </FooterButton>
        ) : (
          <div>
            {renderDownloadReportAction()}
            {renderExportButton()}
          </div>
        )}
      </Footer>
    </Container>
  );
}

export default Final;
